/**
 * PieroZoomer — aiuto alla lettura, globale in tutto il gestionale.
 *
 *  A) Zoom globale  — tasto F4: ogni pressione ingrandisce l'intera interfaccia
 *     a scatti (100% → 200%). Il layout si ridispone.
 *  B) Lente         — tasto F8: attiva/aumenta la lente circolare che segue il cursore
 *     e ingrandisce ESATTAMENTE l'area sotto il mouse. Con la ROTELLA (lente attiva)
 *     si regola finemente l'ingrandimento.
 *  Esc              — spegne tutto.
 *
 * La lente lavora su uno SNAPSHOT pixel-perfect del viewport (html2canvas), catturato
 * a risoluzione maggiorata (oversampling) per restare nitido. Si ricattura su
 * scroll/resize; muovendo il mouse o girando la rotella ridisegna il ritaglio (istantaneo).
 * html2canvas è caricato on-demand al primo uso.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';

const KEY_ZOOM = 'F4';
const KEY_LENS = 'F8';
const ZOOM_STEPS = [1, 1.2, 1.4, 1.6, 1.8, 2];
const LENS_MIN = 1.15;
const LENS_MAX = 4;
const LENS_DEFAULT = 1.25;
const LENS_STEP = 0.35;                // incremento per pressione F8
const WHEEL_STEP = 0.15;               // incremento per tacca di rotella
const LENS_SIZE = 300;                 // diametro lente (px CSS)
const RECAPTURE_MS = 140;              // debounce ricattura su scroll
const DPR = Math.min(2, Math.max(1, typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1));

export default function PieroZoomer() {
  const [zoomIdx, setZoomIdx] = useState(0);
  const [lensOn, setLensOn] = useState(false);
  const [lensZoom, setLensZoom] = useState(LENS_DEFAULT);

  const portalRef = useRef<HTMLElement | null>(null);
  const lensRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const snapRef = useRef<HTMLCanvasElement | null>(null);
  const capScaleRef = useRef(2);         // risoluzione di cattura dello snapshot
  const mouse = useRef({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
  const rafRef = useRef<number | null>(null);
  const capturingRef = useRef(false);
  const recaptureTimer = useRef<number | null>(null);
  const [capturing, setCapturing] = useState(false);

  const zoom = ZOOM_STEPS[zoomIdx];
  const lensZoomRef = useRef(lensZoom);
  lensZoomRef.current = lensZoom;

  if (portalRef.current == null && typeof document !== 'undefined') {
    const el = document.createElement('div');
    el.setAttribute('data-pierozoom', 'root');
    portalRef.current = el;
  }
  useEffect(() => {
    const el = portalRef.current;
    if (!el) return;
    document.documentElement.appendChild(el);
    return () => { el.remove(); };
  }, []);

  useEffect(() => {
    (document.documentElement.style as any).zoom = zoom === 1 ? '' : String(zoom);
    return () => { (document.documentElement.style as any).zoom = ''; };
  }, [zoom]);

  // Disegna il ritaglio ingrandito dello snapshot, centrato sul cursore.
  const draw = useCallback(() => {
    rafRef.current = null;
    const canvas = canvasRef.current;
    const snap = snapRef.current;
    const lens = lensRef.current;
    if (lens) {
      lens.style.left = `${mouse.current.x - LENS_SIZE / 2}px`;
      lens.style.top = `${mouse.current.y - LENS_SIZE / 2}px`;
    }
    if (!canvas || !snap) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const k = lensZoomRef.current;
    const cap = capScaleRef.current;
    const outPx = LENS_SIZE * DPR;
    const srcSize = (LENS_SIZE / k) * cap;           // ritaglio in px snapshot
    // Coordinate DOCUMENTO (client + scroll): lo snapshot è dell'intero documento.
    const cx = (mouse.current.x + window.scrollX) * cap;
    const cy = (mouse.current.y + window.scrollY) * cap;
    ctx.clearRect(0, 0, outPx, outPx);
    ctx.imageSmoothingEnabled = true;
    (ctx as any).imageSmoothingQuality = 'high';
    try {
      ctx.drawImage(snap, cx - srcSize / 2, cy - srcSize / 2, srcSize, srcSize, 0, 0, outPx, outPx);
    } catch { /* snapshot non pronto */ }
  }, []);

  const scheduleDraw = useCallback(() => {
    if (rafRef.current == null) rafRef.current = requestAnimationFrame(draw);
  }, [draw]);

  // Cattura lo snapshot del viewport a risoluzione maggiorata (oversampling adattivo).
  const capture = useCallback(async () => {
    if (capturingRef.current) return;
    capturingRef.current = true;
    setCapturing(true);
    try {
      const de = document.documentElement;
      const dw = Math.max(de.scrollWidth, window.innerWidth);
      const dh = Math.max(de.scrollHeight, window.innerHeight);
      // Cattura l'INTERO documento (non solo il viewport): scrollando la lente continua a
      // funzionare mappando il ritaglio con lo scroll, senza dover ri-catturare.
      // Oversampling adattivo con budget di pixel (~16M) per bilanciare nitidezza/memoria.
      const cap = Math.max(1.2, Math.min(2.5, Math.sqrt(16_000_000 / Math.max(1, dw * dh))));
      capScaleRef.current = cap;
      const html2canvas = (await import('html2canvas')).default;
      const snap = await html2canvas(document.body, {
        width: dw, height: dh,
        scale: cap,
        useCORS: true,
        // Rendering nativo del browser (SVG foreignObject): riproduce fedelmente overflow,
        // scroll container e testi con altezze strette → niente clip spuri come col motore manuale.
        foreignObjectRendering: true,
        backgroundColor: getComputedStyle(document.body).backgroundColor || '#ffffff',
        logging: false,
        ignoreElements: (el) => el.getAttribute?.('data-pierozoom') != null,
      });
      snapRef.current = snap;
      draw();
    } catch { /* mantengo lo snapshot precedente */ }
    finally { capturingRef.current = false; setCapturing(false); }
  }, [draw]);

  const scheduleRecapture = useCallback(() => {
    if (recaptureTimer.current != null) window.clearTimeout(recaptureTimer.current);
    recaptureTimer.current = window.setTimeout(() => { capture(); }, RECAPTURE_MS);
  }, [capture]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === KEY_ZOOM) {
        e.preventDefault();
        setZoomIdx((i) => Math.min(i + 1, ZOOM_STEPS.length - 1));
      } else if (e.key === KEY_LENS) {
        e.preventDefault();
        setLensOn((on) => {
          if (!on) { setLensZoom(LENS_DEFAULT); return true; }
          setLensZoom((z) => Math.min(LENS_MAX, +(z + LENS_STEP).toFixed(2)));
          return true;
        });
      } else if (e.key === 'Escape') {
        setZoomIdx(0);
        setLensOn(false);
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);

  useEffect(() => {
    if (!lensOn) { snapRef.current = null; return; }
    capture();
    const onMove = (e: MouseEvent) => { mouse.current = { x: e.clientX, y: e.clientY }; scheduleDraw(); };
    // Scroll: basta ridisegnare (lo snapshot è dell'intero documento) → fluido, niente
    // ricattura pesante a ogni scroll. Si ricattura solo al resize.
    const onScroll = () => scheduleDraw();
    const onResize = () => scheduleRecapture();
    // Rotella normale → scorre la pagina (la lente segue e si aggiorna); Ctrl/Alt+rotella
    // → regola l'ingrandimento della lente.
    const onWheel = (e: WheelEvent) => {
      if (!(e.ctrlKey || e.altKey)) return;   // lascia scorrere la pagina
      e.preventDefault();
      setLensZoom((z) => Math.max(LENS_MIN, Math.min(LENS_MAX, +(z + (e.deltaY < 0 ? WHEEL_STEP : -WHEEL_STEP)).toFixed(2))));
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true, capture: true });
    window.addEventListener('resize', onResize);
    window.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('wheel', onWheel);
      if (recaptureTimer.current != null) window.clearTimeout(recaptureTimer.current);
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lensOn]);

  useEffect(() => { if (lensOn) scheduleDraw(); }, [lensZoom, lensOn, scheduleDraw]);

  const active = zoom !== 1 || lensOn;
  if (!portalRef.current) return null;

  return createPortal(
    <>
      {lensOn && (
        <div
          ref={lensRef}
          style={{
            position: 'fixed', width: LENS_SIZE, height: LENS_SIZE,
            left: mouse.current.x - LENS_SIZE / 2, top: mouse.current.y - LENS_SIZE / 2,
            borderRadius: '50%', pointerEvents: 'none', zIndex: 2147483646, cursor: 'none',
          }}
        >
          <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', overflow: 'hidden', background: '#fff' }}>
            <canvas ref={canvasRef} width={LENS_SIZE * DPR} height={LENS_SIZE * DPR} style={{ width: LENS_SIZE, height: LENS_SIZE, display: 'block' }} />
          </div>
          <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', boxShadow: '0 0 0 6px rgba(139,92,246,.95), 0 10px 34px rgba(0,0,0,.5)' }} />
          <div style={{ position: 'absolute', left: '50%', top: '50%', width: 12, height: 12, margin: '-6px 0 0 -6px', border: '1px solid rgba(139,92,246,.75)', borderRadius: '50%' }} />
          {capturing && <div style={{ position: 'absolute', right: 10, top: 10, width: 8, height: 8, borderRadius: '50%', background: '#8b5cf6', opacity: .8 }} />}
          {/* Logo PieroZoomer sul bordo inferiore della lente */}
          <img
            src="/assets/pierozoomer-logo.png"
            alt="PieroZoomer"
            draggable={false}
            style={{
              position: 'absolute', left: '50%', bottom: -12, width: 120,
              transform: 'translateX(-50%)', pointerEvents: 'none', userSelect: 'none',
              borderRadius: 8, filter: 'drop-shadow(0 2px 5px rgba(0,0,0,.4))',
            }}
          />
        </div>
      )}

      {active && (
        <div
          style={{
            position: 'fixed', right: 16, bottom: 16, zIndex: 2147483647,
            display: 'flex', gap: 10, alignItems: 'center', padding: '8px 14px',
            borderRadius: 999, background: 'rgba(17,24,39,.92)', color: '#fff',
            font: '600 13px system-ui, sans-serif', boxShadow: '0 6px 24px rgba(0,0,0,.4)', pointerEvents: 'none',
          }}
        >
          <span style={{ color: '#c4b5fd' }}>🔍 PieroZoomer</span>
          {zoom !== 1 && <span>Zoom {Math.round(zoom * 100)}%</span>}
          {lensOn && <span>Lente {lensZoom.toFixed(2)}× · Ctrl+rotella regola · rotella scorre</span>}
          <span style={{ opacity: .6, fontWeight: 400 }}>· Esc per uscire</span>
        </div>
      )}
    </>,
    portalRef.current
  );
}
